/**
 * Remappable control bindings for keyboard, mouse, and gamepad.
 * Persisted in localStorage under CONTROLS_STORAGE_KEY.
 */

export const CONTROLS_STORAGE_KEY = 'minelunky.controls.v1';

/** Semantic gameplay / UI actions. */
export type GameAction =
  | 'moveForward'
  | 'moveBack'
  | 'moveLeft'
  | 'moveRight'
  | 'jump'
  | 'primary'
  | 'inventory'
  | 'interact'
  | 'pause'
  | 'confirm'
  | 'hotbar1'
  | 'hotbar2'
  | 'hotbar3'
  | 'hotbar4'
  | 'hotbar5';

export const GAME_ACTIONS: readonly GameAction[] = [
  'moveForward',
  'moveBack',
  'moveLeft',
  'moveRight',
  'jump',
  'primary',
  'inventory',
  'interact',
  'pause',
  'confirm',
  'hotbar1',
  'hotbar2',
  'hotbar3',
  'hotbar4',
  'hotbar5',
] as const;

export const ACTION_LABELS: Record<GameAction, string> = {
  moveForward: 'Move forward',
  moveBack: 'Move back',
  moveLeft: 'Move left',
  moveRight: 'Move right',
  jump: 'Jump',
  primary: 'Mine / Attack',
  inventory: 'Inventory',
  interact: 'Interact (shop)',
  pause: 'Pause',
  confirm: 'Confirm / Respawn',
  hotbar1: 'Hotbar 1',
  hotbar2: 'Hotbar 2',
  hotbar3: 'Hotbar 3',
  hotbar4: 'Hotbar 4',
  hotbar5: 'Hotbar 5',
};

/** Actions shown in the rebind UI (movement sticks are fixed on gamepad). */
export const REBINDABLE_ACTIONS: readonly GameAction[] = [
  'moveForward',
  'moveBack',
  'moveLeft',
  'moveRight',
  'jump',
  'primary',
  'inventory',
  'interact',
  'pause',
  'confirm',
  'hotbar1',
  'hotbar2',
  'hotbar3',
  'hotbar4',
  'hotbar5',
];

/** Keyboard-only movement actions (gamepad uses left stick). */
export const KEYBOARD_MOVE_ACTIONS: readonly GameAction[] = [
  'moveForward',
  'moveBack',
  'moveLeft',
  'moveRight',
];

export interface ControlSettings {
  version: 1;
  /** Keyboard bindings: KeyboardEvent.code values. */
  keyboard: Record<GameAction, string[]>;
  mouse: {
    /** MouseEvent.button for primary action (0 = LMB). */
    primaryButton: number;
    /** Radians per pixel. */
    sensitivity: number;
    invertY: boolean;
  };
  gamepad: {
    /** Standard Gamepad button indices. */
    buttons: Record<GameAction, number[]>;
    moveDeadzone: number;
    lookDeadzone: number;
    /** Radians per second at full stick deflection. */
    lookSensitivity: number;
    invertY: boolean;
  };
}

export function createDefaultControlSettings(): ControlSettings {
  return {
    version: 1,
    keyboard: {
      moveForward: ['KeyW', 'ArrowUp'],
      moveBack: ['KeyS', 'ArrowDown'],
      moveLeft: ['KeyA', 'ArrowLeft'],
      moveRight: ['KeyD', 'ArrowRight'],
      jump: ['Space'],
      primary: [], // mouse button handles primary by default
      inventory: ['Tab'],
      interact: ['KeyE'],
      pause: ['Escape'],
      confirm: ['Enter', 'Space'],
      hotbar1: ['Digit1'],
      hotbar2: ['Digit2'],
      hotbar3: ['Digit3'],
      hotbar4: ['Digit4'],
      hotbar5: ['Digit5'],
    },
    mouse: {
      primaryButton: 0,
      sensitivity: 0.0022,
      invertY: false,
    },
    gamepad: {
      buttons: {
        moveForward: [],
        moveBack: [],
        moveLeft: [],
        moveRight: [],
        jump: [0], // A / Cross
        primary: [7], // RT / R2
        inventory: [3], // Y / Triangle
        interact: [2], // X / Square
        pause: [9], // Start / Options
        confirm: [0], // A / Cross
        hotbar1: [12], // D-pad up
        hotbar2: [15], // D-pad right
        hotbar3: [13], // D-pad down
        hotbar4: [14], // D-pad left
        hotbar5: [4], // LB
      },
      moveDeadzone: 0.18,
      lookDeadzone: 0.16,
      lookSensitivity: 2.4,
      invertY: false,
    },
  };
}

export function loadControlSettings(): ControlSettings {
  const defaults = createDefaultControlSettings();
  try {
    const raw = localStorage.getItem(CONTROLS_STORAGE_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as Partial<ControlSettings>;
    return mergeControlSettings(defaults, parsed);
  } catch {
    return defaults;
  }
}

export function saveControlSettings(settings: ControlSettings): void {
  localStorage.setItem(CONTROLS_STORAGE_KEY, JSON.stringify(settings));
}

export function resetControlSettings(): ControlSettings {
  const defaults = createDefaultControlSettings();
  saveControlSettings(defaults);
  return defaults;
}

function mergeControlSettings(
  defaults: ControlSettings,
  partial: Partial<ControlSettings>,
): ControlSettings {
  const keyboard = { ...defaults.keyboard };
  if (partial.keyboard) {
    for (const action of GAME_ACTIONS) {
      const codes = partial.keyboard[action];
      if (Array.isArray(codes)) {
        keyboard[action] = codes.filter((c): c is string => typeof c === 'string');
      }
    }
  }

  const mouse = {
    ...defaults.mouse,
    ...(partial.mouse ?? {}),
  };
  if (typeof mouse.sensitivity !== 'number' || !Number.isFinite(mouse.sensitivity)) {
    mouse.sensitivity = defaults.mouse.sensitivity;
  }
  if (typeof mouse.primaryButton !== 'number') {
    mouse.primaryButton = defaults.mouse.primaryButton;
  }
  mouse.invertY = Boolean(mouse.invertY);

  const gpPartial = (partial.gamepad ?? {}) as Partial<ControlSettings['gamepad']>;
  const buttons = { ...defaults.gamepad.buttons };
  if (gpPartial.buttons) {
    for (const action of GAME_ACTIONS) {
      const idxs = gpPartial.buttons[action];
      if (Array.isArray(idxs)) {
        buttons[action] = idxs.filter((n): n is number => typeof n === 'number');
      }
    }
  }

  const gamepad: ControlSettings['gamepad'] = {
    ...defaults.gamepad,
    ...gpPartial,
    buttons,
  };
  for (const key of ['moveDeadzone', 'lookDeadzone', 'lookSensitivity'] as const) {
    if (typeof gamepad[key] !== 'number' || !Number.isFinite(gamepad[key])) {
      gamepad[key] = defaults.gamepad[key];
    }
  }
  gamepad.invertY = Boolean(gamepad.invertY);

  return { version: 1, keyboard, mouse, gamepad };
}

/** Human-readable KeyboardEvent.code label. */
export function formatKeyCode(code: string): string {
  const aliases: Record<string, string> = {
    Space: 'Space',
    Escape: 'Esc',
    Enter: 'Enter',
    Tab: 'Tab',
    ArrowUp: '↑',
    ArrowDown: '↓',
    ArrowLeft: '←',
    ArrowRight: '→',
    Digit1: '1',
    Digit2: '2',
    Digit3: '3',
    Digit4: '4',
    Digit5: '5',
    Digit6: '6',
    Digit7: '7',
    Digit8: '8',
    Digit9: '9',
    Digit0: '0',
  };
  if (aliases[code]) return aliases[code]!;
  if (code.startsWith('Key') && code.length === 4) return code.slice(3);
  if (code.startsWith('Digit') && code.length === 6) return code.slice(5);
  return code;
}

export function formatMouseButton(button: number): string {
  switch (button) {
    case 0:
      return 'LMB';
    case 1:
      return 'MMB';
    case 2:
      return 'RMB';
    case 3:
      return 'Mouse 4';
    case 4:
      return 'Mouse 5';
    default:
      return `Mouse ${button}`;
  }
}

/** Standard gamepad button names (Xbox-style with PlayStation hints). */
export function formatGamepadButton(index: number): string {
  const names: Record<number, string> = {
    0: 'A / Cross',
    1: 'B / Circle',
    2: 'X / Square',
    3: 'Y / Triangle',
    4: 'LB / L1',
    5: 'RB / R1',
    6: 'LT / L2',
    7: 'RT / R2',
    8: 'Select / Share',
    9: 'Start / Options',
    10: 'L3',
    11: 'R3',
    12: 'D-pad ↑',
    13: 'D-pad ↓',
    14: 'D-pad ←',
    15: 'D-pad →',
    16: 'Home',
  };
  return names[index] ?? `Button ${index}`;
}

export function formatKeyboardBinding(codes: string[]): string {
  if (!codes.length) return '—';
  return codes.map(formatKeyCode).join(' / ');
}

export function formatGamepadBinding(indices: number[]): string {
  if (!indices.length) return '—';
  return indices.map(formatGamepadButton).join(' / ');
}
